import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useHeritagePolaroids } from "@/lib/useHeritagePolaroids";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

// Polaroid memory-board for the homepage "Our Heritage" section.
// Renders the CMS-managed `heritage_polaroids` list as slightly rotated
// off-white cards stacked in a small responsive grid. Captions live below
// each photo on a thin handwritten-feel line. The whole wall is editable
// via /admin/heritage.
const HeritagePolaroids = () => {
  const { lang } = useLanguage();
  const { items, loading } = useHeritagePolaroids();

  if (loading || !items.length) return null;

  return (
    <div
      className="mt-12 grid grid-cols-2 items-start gap-x-5 gap-y-8 sm:gap-x-6 sm:gap-y-10 max-w-md md:max-w-none"
      data-testid="heritage-polaroids"
      data-cms-scope="home.heritage.polaroids"
    >
      {items.map((p, i) => {
        const rotation = typeof p.rotation === "number" ? p.rotation : 0;
        // Alternate slight vertical lift so the cards don't sit on a straight baseline.
        const liftClass = i % 2 === 0 ? "md:translate-y-0" : "md:translate-y-6";
        return (
          <figure
            key={p.id || i}
            className={`group relative bg-[#FAF6EE] p-2.5 pb-4 sm:pb-5 shadow-[0_8px_24px_-12px_rgba(26,21,19,0.35)] hover:shadow-[0_16px_32px_-12px_rgba(26,21,19,0.45)] transition-all duration-500 ${liftClass}`}
            style={{
              transform: `rotate(${rotation}deg)`,
              transformOrigin: "center center",
            }}
            data-testid={`heritage-polaroid-${i}`}
          >
            {/* Soft cream paper texture under the photo */}
            <div className="relative aspect-[4/5] overflow-hidden bg-[#E8DFCF]">
              <img
                src={p.image}
                alt={pick(p.caption, lang) || `Heritage memory ${i + 1}`}
                loading="lazy"
                data-cms-role={`polaroid.${p.id || i}`}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              {/* Sepia warm wash to push it toward an archive aesthetic */}
              <div
                className="absolute inset-0 mix-blend-multiply pointer-events-none"
                style={{ background: "linear-gradient(180deg, rgba(255,235,200,0.07) 0%, rgba(140,90,40,0.1) 100%)" }}
              />
              {/* Subtle inner shadow for depth */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{ boxShadow: "inset 0 0 20px rgba(26,21,19,0.18)" }}
              />
              {/* Faint X monogram — integrated into the polaroid frame
                  as if part of the archival stamp. Very low opacity so
                  it whispers rather than competes with the photo. */}
              <BrandMonogramEdge
                size="sm"
                opacity={0.22}
                testIdPrefix={`heritage-polaroid-${i}-brand`}
              />
            </div>
            {/* Caption — handwritten-feel line under the photo. Rendered in
                normal flow (not absolute) so the polaroid grows vertically
                as the caption wraps — no truncation, no ellipsis, no matter
                how long the memory is. `text-wrap: balance` gives a soft
                two-line rhythm when it does wrap. */}
            {pick(p.caption, lang) ? (
              <figcaption
                className="mt-2.5 px-1 text-center text-[11px] sm:text-xs leading-snug text-[#5C5248] italic tracking-wide"
                style={{ textWrap: "balance" }}
                data-testid={`heritage-polaroid-${i}-caption`}
              >
                {pick(p.caption, lang)}
              </figcaption>
            ) : null}
          </figure>
        );
      })}
    </div>
  );
};

export default HeritagePolaroids;
